import { CommonModule } from '@angular/common';
import { Component, signal, computed, inject, DestroyRef, ElementRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { filter } from 'rxjs/operators';

import { AuthService } from '@services/auth.service';
import { LayoutService } from '@services/layout.service';

import { AppPermission } from '@models/auth.model';

import { ROUTE_NAMES } from '../../app.routes-names';

export interface RouteInfo {
  path?: string;
  title: string;
  type: string;
  icontype: string;
  collapse?: string;
  children?: ChildrenItems[];
  fullPath?: string;
  id: string;
  permission?: AppPermission;
}

export interface ChildrenItems {
  path?: string;
  title: string;
  ab?: string;
  type?: string;
  fullPath?: string;
  icontype?: string;
  id: string;
  permission?: AppPermission;
}

export const ROUTES: RouteInfo[] = [
  {
    id: 'agenti',
    fullPath: `/${ROUTE_NAMES.AGENTS.BASE}`,
    title: 'Agenti e Clienti',
    type: 'sub',
    icontype: 'users_single-02',
    collapse: 'agentiSection',
    children: [
      {
        id: 'agenti-lista',
        fullPath: `/${ROUTE_NAMES.AGENTS.BASE}/${ROUTE_NAMES.AGENTS.LIST}`,
        title: 'Agenti',
        icontype: 'users_single-02',
        permission: AppPermission.AGENTS_VIEW,
      },
      {
        id: 'clienti-lista',
        fullPath: `/${ROUTE_NAMES.AGENTS.BASE}/${ROUTE_NAMES.AGENTS.CUSTOMER_LIST}`,
        title: 'Clienti',
        icontype: 'text_align-center',
        permission: AppPermission.CUSTOMERS_VIEW,
      },
      {
        id: 'accounting-estratto-conto',
        fullPath: `/${ROUTE_NAMES.ACCOUNTING.BASE}/${ROUTE_NAMES.ACCOUNTING.ACCOUNT_STATEMENT}`,
        title: 'Estratto conto',
        icontype: 'files_single-copy-04',
        permission: AppPermission.ACCOUNTING_VIEW,
      },
    ],
  },

  {
    id: 'ordini',
    fullPath: `/${ROUTE_NAMES.ORDERS.BASE}`,
    title: 'Ordini',
    type: 'link',
    icontype: 'shopping_cart-simple',
    permission: AppPermission.ORDERS_VIEW,
  },

  {
    id: 'categorie',
    fullPath: `/${ROUTE_NAMES.CATEGORIES.BASE}/${ROUTE_NAMES.CATEGORIES.CATEGORIES_LIST}`,
    title: 'Sconti per Categoria',
    type: 'link',
    icontype: 'shopping_tag-content',
    permission: AppPermission.CATEGORIES_VIEW,
  },

  {
    id: 'prodotti',
    fullPath: `/${ROUTE_NAMES.PRODUCTS.BASE}`,
    title: 'Prodotti',
    type: 'sub',
    icontype: 'design_app',
    collapse: 'prodottiSection',
    children: [
      {
        id: 'prodotti-lista',
        fullPath: `/${ROUTE_NAMES.PRODUCTS.BASE}/${ROUTE_NAMES.PRODUCTS.LIST}`,
        title: 'Prodotti',
        icontype: 'design_app',
        permission: AppPermission.PRODUCTS_VIEW,
      },
      {
        id: 'catalogo-prodotti',
        fullPath: '/catalogo-prodotti/lista',
        title: 'Catalogo Prodotti',
        icontype: 'education_paper',
        permission: AppPermission.PRODUCTS_CATALOG_VIEW,
      },
      {
        id: 'report-vendite',
        fullPath: `/${ROUTE_NAMES.REPORTS.BASE}/${ROUTE_NAMES.REPORTS.LIST}`,
        title: 'Statistiche vendita',
        icontype: 'files_single-copy-04',
        permission: AppPermission.REPORT_VIEW,
      },
    ],
  },

  {
    id: 'marketing',
    fullPath: `/${ROUTE_NAMES.MARKETING.BASE}`,
    title: 'Marketing',
    type: 'sub',
    icontype: 'promotion',
    collapse: 'marketingSection',
    children: [
      {
        id: 'promo',
        fullPath: `/${ROUTE_NAMES.MARKETING.BASE}/${ROUTE_NAMES.MARKETING.LIST}`,
        title: 'Promozioni',
        icontype: 'promotion',
        permission: AppPermission.PROMOTIONS_VIEW,
      },
      {
        id: 'esperto-risponde',
        fullPath: `/${ROUTE_NAMES.MARKETING.FORUM}/lista-domande`,
        title: 'Esperto Risponde',
        icontype: 'files_single-copy-04',
        permission: AppPermission.FORUM_VIEW,
      },
      {
        id: 'push',
        fullPath: `/${ROUTE_NAMES.MARKETING.PUSH_NOTIFICATIONS}/notifiche`,
        title: 'Notifiche Push',
        icontype: 'ui-1_bell-53',
        permission: AppPermission.NOTIFICATIONS_PUSH_MANAGE,
      },
      {
        id: 'documenti',
        fullPath: `/${ROUTE_NAMES.MARKETING.BASE}/${ROUTE_NAMES.MARKETING.DOCUMENTS}`,
        title: 'Documenti',
        icontype: 'files_box',
        permission: AppPermission.DOCUMENTS_MANAGE,
      },
      {
        id: 'gestione-categorie-manutenzione',
        fullPath: `/${ROUTE_NAMES.MAINTENANCE_CATEGORIES.BASE}/${ROUTE_NAMES.MAINTENANCE_CATEGORIES.LIST}`,
        title: 'Categorie Manutenzione',
        icontype: 'design_bullet-list-67',
        permission: AppPermission.MAINTENANCE_CATEGORIES_MANAGE,
      },
    ],
  },

  {
    id: 'app',
    fullPath: `/${ROUTE_NAMES.APP.BASE}`,
    title: 'App',
    type: 'sub',
    icontype: 'education_atom',
    collapse: 'appSection',
    children: [
      {
        id: 'premi',
        fullPath: `/${ROUTE_NAMES.APP.BASE}/${ROUTE_NAMES.APP.GIFT_CATALOGUE.BASE}/${ROUTE_NAMES.APP.GIFT_CATALOGUE.LIST}`,
        title: 'Catalogo Premi',
        icontype: 'objects_diamond',
        permission: AppPermission.GIFT_CATALOGUE_VIEW,
      },
      {
        id: 'punti-categorie',
        fullPath: `/${ROUTE_NAMES.APP.BASE}/${ROUTE_NAMES.APP.POINTS.BASE}/${ROUTE_NAMES.APP.POINTS.CATEGORIES}/${ROUTE_NAMES.CATEGORIES.CATEGORIES_LIST}`,
        title: 'Punti Categorie',
        icontype: 'points-category',
        permission: AppPermission.POINTS_CATEGORIES_VIEW,
      },
      {
        id: 'punti-app',
        fullPath: `/${ROUTE_NAMES.APP.APP_POINTS}/lista`,
        title: 'Punti APP',
        icontype: 'points-app',
        permission: AppPermission.POINTS_APP_VIEW,
      },
      {
        id: 'punti-prodotti',
        fullPath: `/${ROUTE_NAMES.APP.BASE}/${ROUTE_NAMES.APP.POINTS.BASE}/${ROUTE_NAMES.APP.POINTS.PRODUCT}`,
        title: 'Punti Prodotti',
        icontype: 'points-products',
        permission: AppPermission.POINTS_MANAGE,
      },
      {
        id: 'prodotti-evidenza',
        fullPath: `/${ROUTE_NAMES.APP.FEATURED_PRODUCTS}/lista`,
        title: 'Prodotti in evidenza',
        icontype: 'design_image',
        permission: AppPermission.FEATURED_PRODUCTS_MANAGE,
      },
      {
        id: 'prodotti-correlati',
        fullPath: `/${ROUTE_NAMES.APP.RELATED_PRODUCTS}/lista`,
        title: 'Prodotti Correlati',
        icontype: 'design_vector',
        permission: AppPermission.RELATED_PRODUCTS_MANAGE,
      },
      {
        id: 'annunci-mercatino',
        fullPath: `/${ROUTE_NAMES.APP.MARKETPLACE_ANNOUNCEMENTS}/lista`,
        title: 'Annunci Mercatino',
        icontype: 'shopping_cart-simple',
        permission: AppPermission.MARKETPLACE_ANNOUNCEMENTS_VIEW,
      },
      {
        id: 'gestione-categorie',
        fullPath: `/${ROUTE_NAMES.APP.MARKETPLACE_CATEGORIES}/lista`,
        title: 'Categorie',
        icontype: 'design_bullet-list-67',
        permission: AppPermission.MARKETPLACE_CATEGORIES_VIEW,
      },
      {
        id: 'app-versions',
        fullPath: `/${ROUTE_NAMES.APP.VERSIONS}/lista`,
        title: 'Versioni App',
        icontype: 'ui-1_settings-gear-63',
        permission: AppPermission.APP_VERSIONS_VIEW,
      },
    ],
  },

  {
    id: 'users',
    fullPath: `/${ROUTE_NAMES.USERS.BASE}`,
    title: 'Gestione utenti',
    type: 'link',
    icontype: 'users_single-02',
    permission: AppPermission.USERS_MANAGE,
  },
];

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, NgbModule],
})
export class SidebarComponent {
  private authService = inject(AuthService);
  private layoutService = inject(LayoutService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private element = inject<ElementRef<HTMLElement>>(ElementRef);

  public isCollapsed = signal(true);
  public sidebarMiniActive = this.layoutService.isSidebarMini;

  // Gestione stato collasso per sezioni
  public collapsedSections = signal<Record<string, boolean>>({
    agentiSection: true,
    prodottiSection: true,
    marketingSection: true,
    appSection: true,
  });

  public menuItems = computed(() => {
    return ROUTES.filter((item) => {
      // Principal permission check
      if (item.permission && !this.authService.hasPermission(item.permission)) {
        return false;
      }
      // If it's a sub-menu, ensure it has at least one visible child
      if (item.type === 'sub') {
        return item.children?.some((child) => !child.permission || this.authService.hasPermission(child.permission));
      }
      return true;
    }).map((item) => ({
      ...item,
      // Filter children by permission
      children: item.children?.filter((child) => !child.permission || this.authService.hasPermission(child.permission)),
    }));
  });

  toggleCollapse(section: string) {
    if (!section) return;
    this.collapsedSections.update((sections) => ({
      ...sections,
      [section]: !sections[section],
    }));
  }

  constructor() {
    // Keep the active sidebar item visible after navigation without forcing it to the top
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        // find the active anchor inside this sidebar
        const el = (this.element.nativeElement as HTMLElement).querySelector('.sidebar-wrapper .nav li.active a');
        if (el instanceof HTMLElement) {
          // scroll the element into view but keep it as 'nearest' to avoid jumping to top
          try {
            el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
          } catch (e) {
            // fallback for older browsers
            const parent = (this.element.nativeElement as HTMLElement).querySelector('.sidebar-wrapper');
            if (parent instanceof HTMLElement) {
              const parentRect = parent.getBoundingClientRect();
              const elRect = el.getBoundingClientRect();
              parent.scrollTop += elRect.top - parentRect.top - parent.clientHeight / 2;
            }
          }
        }
      });
  }

  minimizeSidebar() {
    this.layoutService.toggleSidebarMini();
  }
}
